import React, { Component } from "react";
import cn from "classnames";
import { translate } from "react-i18next";
import _debounce from "lodash/debounce";
import { provideAuth } from "shared/context/Auth";
import { provideTheme } from "shared/context/Theme";
import Icon from "backpack-ui/dist/components/icon";
import { Container, Divider } from "../../layout";
import Portal from "../Portal";
import Logo from "../Logo";
import Overlay from "../Overlay";
import Button from "./Button";
import navigation from "./navigation";
import { SEARCH_HOST } from "./navigation/config";
import search from "./search";
import { getAutocomplete } from "./search/service";
import styles from "./styles.css";
import themes from "./themes";

export class GlobalHeader extends Component {
  constructor(props) {
    super(props);

    this.header = React.createRef();
    this.state = {
      mobileNavOpen: false,
      mobileSearchOpen: false,
      offsetTop: this.getOffsetTop(),
      searchTerm: "",
      searchResults: [],
    };

    this.body = null;
    this.debouncedFetch = _debounce(this.fetchResults, 100);
  }

  componentDidMount() {
    if (typeof document !== "undefined") {
      this.body = document.body;
    }
  }

  fetchResults = (value) => {
    const { searchHost } = this.props;
    return getAutocomplete(value, searchHost).then((json) => {
      this.setState({
        searchResults: json.data,
      });
    });
  };

  toggleScrollLocked = () => {
    if (this.body) {
      if (this.body.classList.contains("scroll-locked")) {
        this.body.classList.remove("scroll-locked");
      } else {
        this.body.classList.add("scroll-locked");
      }
    }
  };

  getOffsetTop = () =>
    this.header.current ? this.header.current.getBoundingClientRect().top : 0;

  toggleMobileNav = (event) => {
    const { mobileNavOpen } = this.state;

    event.preventDefault();

    this.setState(
      {
        mobileNavOpen: !mobileNavOpen,
      },
      this.toggleScrollLocked
    );
  };

  toggleMobileSearch = (event) => {
    const { mobileSearchOpen } = this.state;

    event.preventDefault();

    this.setState(
      {
        mobileSearchOpen: !mobileSearchOpen,
        offsetTop: this.getOffsetTop(),
      },
      this.toggleScrollLocked
    );
  };

  dismissOverlay = (event) => {
    event.preventDefault();

    this.setState(
      {
        mobileNavOpen: false,
        mobileSearchOpen: false,
      },
      this.toggleScrollLocked
    );
  };

  handleSearchChange = ({ target }) => {
    const { value } = target;
    this.setState({
      searchTerm: value,
    });
    this.debouncedFetch(value);
  };

  render() {
    const { theme } = this.props;
    const {
      mobileNavOpen,
      mobileSearchOpen,
      offsetTop,
      searchTerm,
      searchResults,
    } = this.state;

    return (
      <header
        className={theme.styles.globalHeader}
        role="banner"
        ref={this.header}
      >
        <Container className={theme.styles.container}>
          <Logo className={theme.styles.logo} />

          <React.Fragment key="desktop">
            <search.Desktop
              className={theme.styles.searchDesktop}
              searchTerm={searchTerm}
              searchResults={searchResults}
              onChange={this.handleSearchChange}
            />
            <navigation.Desktop className={theme.styles.navigationDesktop} />
          </React.Fragment>

          <React.Fragment key="mobile">
            <Button
              element="a"
              className={theme.styles.searchButtonMobile}
              href={SEARCH_HOST}
              onClick={this.toggleMobileSearch}
            >
              <Icon.Search label="Open search" />
            </Button>

            <Button
              element="a"
              className={theme.styles.menuButtonMobile}
              href="#footer"
              onClick={this.toggleMobileNav}
            >
              <Icon.Menu label="Open navigation menu" />
            </Button>

            {mobileSearchOpen && (
              <Portal key="mobile-search">
                <search.Mobile
                  className={cn(
                    theme.styles.searchMobile,
                    theme.styles.searchMobileVisible
                  )}
                  style={{ top: `${offsetTop}px` }}
                  searchTerm={searchTerm}
                  searchResults={searchResults}
                  onChange={this.handleSearchChange}
                  onClose={this.toggleMobileSearch}
                />

                <Overlay
                  onClick={this.dismissOverlay}
                  className={cn(
                    theme.styles.overlayMobile,
                    theme.styles.overlayMobileVisible
                  )}
                  style={{ top: `${offsetTop}px` }}
                />
              </Portal>
            )}

            {mobileNavOpen && (
              <Portal key="mobile-navigation">
                <navigation.Mobile
                  className={cn(
                    theme.styles.navigationMobile,
                    theme.styles.navigationMobileVisible
                  )}
                  isOpen={mobileNavOpen}
                />

                <Overlay
                  onClick={this.dismissOverlay}
                  className={cn(
                    theme.styles.overlayMobile,
                    theme.styles.overlayMobileVisible
                  )}
                />
              </Portal>
            )}
          </React.Fragment>
        </Container>
        {theme.name !== "blue" && (
          <Divider className={theme.styles.divider} container />
        )}
      </header>
    );
  }
}

export default translate()(
  provideAuth(provideTheme(GlobalHeader, styles, themes))
);
